// Animated data-flow diagram for the security section: the add-in asks
// Martinci for the signature with nothing but the sender's address, and the
// message itself goes straight from Outlook to Microsoft 365 — no relay.
// Two hand-tuned layouts (wide / portrait) instead of one SVG scaled down.
import React from 'react';
import { useT } from '../../../i18n';
import './ArchitectureDiagram.css';

const LAYOUTS = {
  wide: {
    w: 960,
    h: 430,
    node: { w: 196, h: 72 },
    nodes: {
      outlook: [150, 235],
      martinci: [500, 78],
      m365: [560, 340],
      rcpt: [850, 340],
    },
    paths: {
      req: 'M150 199 C 190 110, 320 78, 402 78',
      mail: 'M248 250 C 340 340, 400 340, 462 340',
      deliver: 'M658 340 L752 340',
      relay: 'M520 114 L548 304',
    },
    labels: [
      { x: 230, y: 112, t: 'reqWide', k: 'req', anchor: 'end' },
      { x: 330, y: 160, t: 'res', k: 'res', anchor: 'middle' },
      { x: 330, y: 372, t: 'mailWide', k: 'mail', anchor: 'middle' },
      { x: 705, y: 318, t: 'deliver', k: 'mail', anchor: 'middle' },
      { x: 560, y: 214, t: 'relay', k: 'relay', anchor: 'start' },
    ],
    cross: [534, 209],
  },
  tall: {
    w: 360,
    h: 640,
    node: { w: 160, h: 62 },
    nodes: {
      outlook: [96, 70],
      martinci: [264, 230],
      m365: [96, 400],
      rcpt: [264, 570],
    },
    paths: {
      req: 'M176 70 C 240 70, 264 120, 264 199',
      mail: 'M96 101 L96 369',
      deliver: 'M96 431 C 96 500, 264 480, 264 539',
      relay: 'M240 261 L130 369',
    },
    labels: [
      { x: 274, y: 140, t: 'reqTall', k: 'req', anchor: 'start' },
      { x: 274, y: 160, t: 'res', k: 'res', anchor: 'start' },
      { x: 84, y: 240, t: 'mailTall1', k: 'mail', anchor: 'end' },
      { x: 84, y: 260, t: 'mailTall2', k: 'mail', anchor: 'end' },
      { x: 200, y: 352, t: 'relay', k: 'relay', anchor: 'start' },
    ],
    cross: [185, 315],
  },
};

const messages = {
  nodes: {
    tr: {
      outlook: ['Outlook', 'Masaüstü · Web · Mobil'],
      martinci: ['Martinci', 'İmza kuralları'],
      m365: ['Microsoft 365', 'Exchange Online'],
      rcpt: ['Alıcı', 'İmzalı e-posta'],
    },
    en: {
      outlook: ['Outlook', 'Desktop · Web · Mobile'],
      martinci: ['Martinci', 'Signature rules'],
      m365: ['Microsoft 365', 'Exchange Online'],
      rcpt: ['Recipient', 'Signed email'],
    },
  },
  reqWide: { tr: 'Sadece gönderen adresi', en: 'Sender address only' },
  reqTall: { tr: 'Sadece adres', en: 'Address only' },
  res: { tr: '← İmza HTML’i', en: '← Signature HTML' },
  mailWide: { tr: 'E-posta doğrudan Microsoft’a', en: 'Email goes straight to Microsoft' },
  mailTall1: { tr: 'E-posta', en: 'Email goes' },
  mailTall2: { tr: 'doğrudan', en: 'direct' },
  deliver: { tr: 'Teslim', en: 'Delivered' },
  relay: { tr: 'Relay yok', en: 'No relay' },
  aria: {
    tr: 'Outlook imzayı Martinci\'den yalnızca gönderen adresiyle ister; e-posta doğrudan Microsoft 365 üzerinden alıcıya gider.',
    en: 'Outlook asks Martinci for the signature with only the sender address; the email goes straight through Microsoft 365 to the recipient.',
  },
};

function Diagram({ id, layout }) {
  const { t } = useT(messages);
  const nodes = t('nodes');
  const L = LAYOUTS[layout];
  const { w: nw, h: nh } = L.node;
  return (
    <svg viewBox={`0 0 ${L.w} ${L.h}`} className={`lp-arch-svg is-${layout}`} role="img" aria-label={t('aria')}>
      <defs>
        <radialGradient id={`${id}-glow`}>
          <stop offset="0%" className="lp-arch-glow-stop" />
          <stop offset="100%" className="lp-arch-glow-stop is-end" />
        </radialGradient>
      </defs>

      <circle cx={L.nodes.martinci[0]} cy={L.nodes.martinci[1]} r={nw * 0.75} fill={`url(#${id}-glow)`} />

      {Object.entries(L.paths).map(([k, d]) => (
        <path key={k} id={`${id}-${k}`} d={d} className={`lp-arch-path is-${k}`} />
      ))}

      {/* request → / ← response on the same curve */}
      <circle r="5" className="lp-arch-dot is-req">
        <animateMotion dur="2.4s" repeatCount="indefinite" keyPoints="0;1" keyTimes="0;1" calcMode="linear">
          <mpath href={`#${id}-req`} />
        </animateMotion>
      </circle>
      <circle r="5" className="lp-arch-dot is-res">
        <animateMotion dur="2.4s" begin="1.2s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
          <mpath href={`#${id}-req`} />
        </animateMotion>
      </circle>
      {[0, 0.8, 1.6].map((b) => (
        <rect key={b} x="-9" y="-6" width="18" height="12" rx="2" className="lp-arch-mail">
          <animateMotion dur="2.4s" begin={`${b}s`} repeatCount="indefinite" rotate="auto">
            <mpath href={`#${id}-mail`} />
          </animateMotion>
        </rect>
      ))}
      <rect x="-9" y="-6" width="18" height="12" rx="2" className="lp-arch-mail">
        <animateMotion dur="1.2s" begin="2.4s" repeatCount="indefinite" rotate="auto">
          <mpath href={`#${id}-deliver`} />
        </animateMotion>
      </rect>

      <g className="lp-arch-cross" transform={`translate(${L.cross[0]} ${L.cross[1]})`}>
        <circle r="14" />
        <path d="M-5 -5 L5 5 M5 -5 L-5 5" />
      </g>

      {Object.entries(L.nodes).map(([k, [x, y]]) => (
        <g key={k} className={`lp-arch-node is-${k}`} transform={`translate(${x - nw / 2} ${y - nh / 2})`}>
          <rect width={nw} height={nh} rx={nh / 2} />
          <text x={nw / 2} y={nh / 2 - 4} textAnchor="middle" className="lp-arch-node-title">{nodes[k][0]}</text>
          <text x={nw / 2} y={nh / 2 + 16} textAnchor="middle" className="lp-arch-node-sub">{nodes[k][1]}</text>
        </g>
      ))}

      {L.labels.map((lb) => (
        <text key={lb.t + lb.y} x={lb.x} y={lb.y} textAnchor={lb.anchor} className={`lp-arch-label is-${lb.k}`}>{t(lb.t)}</text>
      ))}
    </svg>
  );
}

export function ArchitectureDiagram() {
  return (
    <div className="lp-arch">
      <Diagram id="lp-arch-w" layout="wide" />
      <Diagram id="lp-arch-t" layout="tall" />
    </div>
  );
}

export default ArchitectureDiagram;
